<template>
    <Portal to="sidepanel">
        <SidePanel
            :key="`addExamSidePanel-${String($.vnode.key)}`"
            name="Add Exam"
            class="add-exam"
            width="medium"
            :is-dark-mode="isDarkMode"
            :class="{ 'add-exam--full-height': showFullHeight }"
            @close="emitClose"
        >
            <div v-if="selectedExamVersions.length > 1" class="add-exam__versions">
                <div class="add-exam__title">
                    Choose Exam Version
                </div>
                <div class="add-exam__description">
                    Which version {{ examName }} is right for you?
                    You can change your exam version at any time in settings.
                </div>
                <PocketButton
                    type="icon"
                    icon="arrow"
                    class="add-exam__back"
                    @click="selectedExam = null"
                >
                    Back to Browse
                </PocketButton>
                <ExamVersionCard
                    v-for="(exam, index) in selectedExamVersions"
                    :key="exam.objectId"
                    :exam="exam"
                    :adding-exam="true"
                    :is-selected-version="selectedVersion === exam.version"
                    :is-latest-version="index === 0"
                    :is-dark-mode="isDarkMode"
                    @clickExamVersion="selectVersion"
                />
            </div>
            <!-- @todo update uikit to support nest-server types -->
            <BundleSearch
                v-else
                :exam-metadata-by-id="examMetadata as Record<string, Study.Class.ExamMetadataJSON>"
                :bundles="bundles as Study.Class.BundleJSON[]"
                :premium-bundle-ids="premiumBundleIds"
                :premium-exam-ids="premiumExamIds"
                class="add-exam__bundle-search"
                :is-dark-mode="isDarkMode"
                @select="selectExam"
            />
            <template #action>
                <PocketButton
                    data-test-id="add-exam-side-panel-add-exam-button"
                    :is-dark-mode="isDarkMode"
                    :disabled="!selectedExam || (selectedExamVersions.length > 1 && !selectedVersion)"
                    :is-loading="isLoading"
                    @click="addExam"
                >
                    Add Exam
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import UIKit from '@pocketprep/ui-kit'
import type { TBundleExam } from '@/components/Register/ChooseExam.vue'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { userModule } from '@/store/user/module'
import { subscriptionModule } from '@/store/subscription/module'
import { toastModule } from '@/store/toast/module'
import ExamVersionCard from '@/components/Settings/EditExam/ExamVersionCard.vue'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import { examVersionNativeAppName } from '@/utils'

const {
    SidePanel,
    Button: PocketButton,
    BundleSearch,
} = UIKit

withDefaults(
    defineProps<{
        showFullHeight?: boolean
    }>(),
    {
        showFullHeight: false,
    }
)

const emit = defineEmits<{
    close: []
}>()

const isLoading = ref(true)
const selectedExam = ref<TBundleExam | null>(null)
const selectedVersion = ref<null | string>(null)
const examName = ref<null | string>(null)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const bundles = computed(() => {
    return [ ...bundleModule.getters.getBundles() ]
        .filter(bundle => !bundleModule.getters.isBundleDisabled(bundle.name))
        .sort((a, b) => a.name.localeCompare(b.name))
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getMostRecentExamMetadataById({ excludeDiscontinued: true })
})

const premiumBundleIds = computed<string[]>(() => {
    // UI-Kit BundleSearch :premium-bundle-ids prop is expecting an string[] | undefined
    return subscriptionModule.getters.premiumBundleIds().filter(id => typeof id === 'string')
})

const premiumExamIds = computed<string[]>(() => {
    // UI-Kit BundleSearch :premium-exam-ids prop is expecting an string[] | undefined
    return subscriptionModule.getters.premiumExamIds().filter(id => typeof id === 'string')
})

const selectedExamBundle = computed(() => {
    return bundles.value.find(b => b.exams.find(e => e.objectId === selectedExam.value?.objectId))
})

const selectedExamVersions = computed(() => {
    return ([ ...selectedExamBundle.value?.exams.map(e =>
        examMetadataModule.getters.getExamMetadataById({ excludeDiscontinued: true })[e.objectId]
    ) || [] ])
        .filter(e => e && e.examGuid === selectedExam.value?.examGuid)
        .sort((a, b) => -a.version.localeCompare(b.version, undefined, { numeric: true })) || []
})

// This is the full exam object not just the exam's version
const newestMajorVersionExam = computed(() => {
    return selectedExamVersions.value[0]
})

const hasSelectedNewestMajorVersion = computed(() => {
    return selectedVersion.value?.split('.')[0] === newestMajorVersionExam.value.version.split('.')[0]
})

onMounted(async () => {
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
        userModule.actions.fetchUserData(),
    ])

    isLoading.value = false
})

const selectVersion = (exam: Study.Class.ExamMetadataJSON | ClientExam) => {
    if (!selectedExamBundle.value) {
        return
    }

    selectedVersion.value = exam.version
    selectedExam.value = {
        ...exam,
        bundle: selectedExamBundle.value,
    }
}

const selectExam = (exam: TBundleExam) => {
    selectedExam.value = exam
}

const updateWebConfigMessageSeen = async () => {
    if (!selectedExam.value?.minorVersionMessaging && hasSelectedNewestMajorVersion.value) {
        return
    }

    if (!hasSelectedNewestMajorVersion.value) {
        await userModule.actions.markNextMajorVersionSeen(newestMajorVersionExam.value)
    }

    if (selectedExam.value?.minorVersionMessaging) {
        const examMinorVersionMessage = selectedExam.value?.minorVersionMessaging?.type
        const typeOfMinorMessage = examMinorVersionMessage === 'newQuestions'
            ? 'minorNewQuestions'
            : 'minorNewMockExams'
        const hasSeenMinorExamVersionMessage =
            userModule.getters.getHasSeenMessage(typeOfMinorMessage, selectedExam.value)

        if (!hasSeenMinorExamVersionMessage) {
            await userModule.actions.markMessageSeen(typeOfMinorMessage)
        }
    }
}

const addExam = async () => {
    isLoading.value = true
    const exam = selectedExam.value
    if (!exam) {
        isLoading.value = false
        return
    }
    examMetadataModule.state.isSwitchingExams = true
    await userModule.actions.updateCurrentExam(exam.objectId)
    const premiumMessage = subscriptionModule.getters.getSubscriptionForExamId(exam.objectId)
        ? ''
        : ' Upgrade to Premium to get the full experience!'
    toastModule.actions.displayToast({
        title: `${exam.nativeAppName} added successfully`,
        subtext: `You are now studying the ${exam.nativeAppName} exam.${premiumMessage}`,
    })
    await updateWebConfigMessageSeen()
    emitClose()
    isLoading.value = false
    examMetadataModule.state.isSwitchingExams = false
}

const emitClose = () => {
    emit('close')
}

const checkSelectedExam = () => {
    if (selectedExam.value) {
        examName.value = examVersionNativeAppName(selectedExam.value?.nativeAppName)
    }
}

watch(selectedExam, checkSelectedExam)
</script>
<style lang="scss" scoped>
.add-exam {
    &--full-height {
        top: 0;
        height: 100%;
        z-index: 1003;

        @include breakpoint(black-bear) {
            height: calc(100% - 10px);
        }
    }

    &__title {
        font-size: 15px;
        line-height: 18px;
        margin-bottom: 8px;
        font-weight: 600;
    }

    &__description {
        font-size: 14px;
        line-height: 18px;
        margin-bottom: 16px;
        color: $ash;
    }

    &__back {
        margin-bottom: 14px;
        margin-left: -14px;

        :deep(svg) {
            transform: rotate(180deg);
        }
    }

    &__bundle-search {
        height: auto;
        padding-bottom: 50px;
        margin: 0 -20px;

        @include breakpoint(black-bear) {
            width: calc(100% + 18px);
            margin: 0 -9px;
        }
    }
}
</style>
